import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  List,
  Sparkles,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import LoginPromptModal from '../components/LoginPromptModal';
import AdBanner from '../components/AdBanner';
import SeoHead from '../components/SeoHead';
import '../styles/chapter-viewer.css';

const ChapterViewer = () => {
  const { bookId, chapterNum, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const isSushruta = location.pathname.includes('/sushruta-samhita');
  const activeChapterNum = parseInt(chapterNum || '1', 10);

  // Core state
  const [bookInfo, setBookInfo] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reading UX & Theme state
  const [theme, setTheme] = useState('night'); // 'night', 'sepia', 'light', 'dark'
  const [fontSize, setFontSize] = useState(14);
  const [tocOpen, setTocOpen] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [mobileAiOpen, setMobileAiOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // AI & Interactive state
  const [selectedText, setSelectedText] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');

  const articleRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(null);

  const closeMobileDrawers = () => {
    setMobileTocOpen(false);
    setMobileAiOpen(false);
  };

  // Sync article card height with right sidebar height
  useEffect(() => {
    const updateHeight = () => {
      if (rightSidebarRef.current && window.innerWidth > 768) {
        const h = rightSidebarRef.current.offsetHeight;
        if (h && h > 200) {
          setSidebarHeight(h);
        }
      } else {
        setSidebarHeight(null);
      }
    };

    updateHeight();
    const timer1 = setTimeout(updateHeight, 200);
    const timer2 = setTimeout(updateHeight, 600);
    window.addEventListener('resize', updateHeight);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('resize', updateHeight);
    };
  }, [currentChapter, loading, isAuthenticated]);

  // Load Chapter details (supports both Slug lookup & ID lookup)
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (slug) {
        let chData;
        try {
          chData = await apiService.getTextbookChapterBySlug(slug);
        } catch (err) {
          chData = await apiService.getChapterBySlug(slug);
        }
        setCurrentChapter(chData);

        if (chData.textbookId) {
          const bookData = await apiService.getTextbookWithChapters(chData.textbookId);
          setBookInfo(bookData);
          setChapters(bookData.chapters || []);
        } else if (chData.bookId) {
          const bookData = await apiService.getBookWithChapters(chData.bookId);
          setBookInfo(bookData);
          setChapters(bookData.chapters || []);
        }
      } else {
        let bookData;
        if (isSushruta) {
          bookData = await apiService.getBookWithChapters(bookId);
        } else {
          bookData = await apiService.getTextbookWithChapters(bookId);
        }
        setBookInfo(bookData);
        const chList = bookData.chapters || [];
        setChapters(chList);

        let chData;
        if (isSushruta) {
          chData = await apiService.getChapter(bookId, activeChapterNum);
        } else {
          const targetChapterMeta = chList.find(c => c.chapterNumber === activeChapterNum);
          if (targetChapterMeta) {
            chData = await apiService.getTextbookChapterById(targetChapterMeta.id);
          } else {
            chData = await apiService.getTextbookChapterById(chList[0]?.id || 1);
          }
        }
        setCurrentChapter(chData);
      }
    } catch (err) {
      console.error('Error loading chapter viewer data:', err);
      setError(err.message || 'Failed to load chapter content.');
    } finally {
      setLoading(false);
    }
  }, [slug, bookId, activeChapterNum, isSushruta]);

  useEffect(() => {
    loadData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    closeMobileDrawers();
  }, [loadData]);

  // Save last read progress to localStorage
  useEffect(() => {
    if (currentChapter && bookInfo) {
      try {
        const historyKey = `vedic_read_history_${bookInfo.id || 'book'}`;
        const historyData = {
          bookId: bookInfo.id,
          bookTitle: bookInfo.title,
          chapterId: currentChapter.id,
          chapterNumber: currentChapter.chapterNumber,
          chapterTitle: currentChapter.chapterTitle,
          chapterSlug: currentChapter.slug,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(historyKey, JSON.stringify(historyData));
        localStorage.setItem('vedic_last_read_global', JSON.stringify(historyData));
      } catch (err) {
        console.error('Error saving reading progress:', err);
      }
    }
  }, [currentChapter, bookInfo]);

  // Track Reading Progress on Scroll
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Compute Prev and Next chapters for stepper
  const currentIndex = chapters.findIndex(c => c.id === currentChapter?.id);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex >= 0 && currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  const navigateToChapter = (ch) => {
    closeMobileDrawers();
    if (ch.slug) {
      if (isSushruta) {
        navigate(`/sushruta-samhita/${ch.slug}`);
      } else {
        navigate(`/textbooks/${ch.slug}`);
      }
    } else if (bookInfo?.id) {
      if (isSushruta) {
        navigate(`/sushruta-samhita/${bookInfo.id}/chapter/${ch.chapterNumber}`);
      } else {
        navigate(`/textbooks/${bookInfo.id}/chapter/${ch.chapterNumber}`);
      }
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection) {
      const text = selection.toString().trim();
      if (text.length > 3 && text.length < 500) {
        setSelectedText(text);
      }
    }
  };

  const handleAiAction = async (actionType) => {
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      setAiLoading(true);
      setAiError(null);
      let res;
      if (actionType === 'translate') {
        res = await apiService.analyzeReaderText(selectedText || currentChapter?.chapterTitle, actionType);
      } else if (actionType === 'commentary') {
        res = await apiService.analyzeReaderText(selectedText || currentChapter?.chapterTitle, actionType);
      } else {
        res = await apiService.analyzeReaderText(selectedText || currentChapter?.chapterTitle, actionType);
      }
      setAiResponse(res);
      setChatHistory([{ role: 'assistant', content: res, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    } catch (err) {
      setAiError(err.message || 'AI request failed.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }

    const userMsg = { role: 'user', content: chatMessage };
    setChatHistory((prev) => [...prev, userMsg]);
    setChatMessage('');
    setAiLoading(true);

    try {
      const res = await apiService.analyzeReaderText(chatMessage, 'chat');
      const botMsg = { role: 'assistant', content: res || 'No response available.' };
      setChatHistory((prev) => [...prev, botMsg]);
    } catch (err) {
      setChatHistory((prev) => [...prev, { role: 'assistant', content: 'Sorry, failed to get an answer. Please try again.' }]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="chapter-viewer-page">
      <SeoHead
        title={`${currentChapter ? `Chapter ${currentChapter.chapterNumber}: ${currentChapter.chapterTitle}` : 'Chapter Viewer'} | Vedic Surgery`}
        description={currentChapter?.summary || `Read Maharishi Sushruta's Ayurvedic surgical chapter with AI translations.`}
      />

      {/* Reading Progress Indicator */}
      <div className="reading-progress-track">
        <div className="reading-progress-fill" style={{ width: `${scrollProgress}%` }} />
      </div>

      {/* Sticky Header Control Toolbar */}
      <div className="cv-toolbar">
        <div className="cv-toolbar-inner">
          <nav className="cv-breadcrumbs" aria-label="Breadcrumb">
            <Link to={isSushruta ? '/sushruta-samhita' : '/textbooks'}>
              {isSushruta ? 'Sushruta Samhita' : 'Textbooks'}
            </Link>
            <span className="separator">/</span>
            <span className="current">
              {currentChapter ? `Ch ${currentChapter.chapterNumber}: ${currentChapter.chapterTitle}` : 'Loading...'}
            </span>
          </nav>

          <div className="cv-controls">
            {/* TOC Toggle Button */}
            <button
              type="button"
              className={`cv-control-btn ${tocOpen || mobileTocOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileTocOpen((v) => !v);
                  setMobileAiOpen(false);
                } else {
                  setTocOpen((v) => !v);
                }
              }}
            >
              <List size={16} />
              <span>Chapters ({chapters.length})</span>
            </button>

            {/* Font Size Adjusters */}
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.max(12, s - 1))}
              title="Decrease font size"
            >
              A-
            </button>
            <button
              type="button"
              className="cv-control-btn cv-font-btn"
              onClick={() => setFontSize((s) => Math.min(22, s + 1))}
              title="Increase font size"
            >
              A+
            </button>

            {/* Theme Selector */}
            <div className="cv-theme-selector">
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'night' ? 'active' : ''}`}
                onClick={() => setTheme('night')}
                style={{ background: '#0b0f19' }}
                title="Night theme"
              >
                <Moon size={12} color="#ffffff" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'sepia' ? 'active' : ''}`}
                onClick={() => setTheme('sepia')}
                style={{ background: '#fbf0d9' }}
                title="Sepia theme"
              >
                <Sun size={12} color="#432818" />
              </button>
              <button
                type="button"
                className={`cv-theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                style={{ background: '#ffffff' }}
                title="Light theme"
              >
                <Sun size={12} color="#000000" />
              </button>
            </div>

            {/* AI Assistant Drawer Trigger */}
            <button
              type="button"
              className={`cv-control-btn ${mobileAiOpen ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth <= 1024) {
                  setMobileAiOpen((v) => !v);
                  setMobileTocOpen(false);
                } else if (!isAuthenticated()) {
                  setShowLoginPrompt(true);
                } else {
                  const aiWidget = document.querySelector('.cv-ai-widget');
                  if (aiWidget) {
                    aiWidget.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
            >
              <Sparkles size={16} color="var(--primary-color)" />
              <span>Vedic AI</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Backdrop Overlay for Drawers */}
      {(mobileTocOpen || mobileAiOpen) && (
        <div className="cv-mobile-backdrop" onClick={closeMobileDrawers} />
      )}

      {/* Main Reading Container Layout */}
      <div className={`cv-container ${!tocOpen ? 'toc-closed' : ''}`}>

        {/* Collapsible Left TOC Sidebar / Mobile Drawer */}
        {(tocOpen || mobileTocOpen) && (
          <aside className={`cv-toc-sidebar ${mobileTocOpen ? 'mobile-open' : ''}`}>
            <div className="cv-toc-header">
              <h3>Chapter Index</h3>
              <span className="cv-toc-badge">{chapters.length} Ch.</span>
            </div>
            <div className="cv-toc-list">
              {chapters.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  className={`cv-toc-item ${ch.id === currentChapter?.id ? 'active' : ''}`}
                  onClick={() => {
                    closeMobileDrawers();
                    navigateToChapter(ch);
                  }}
                >
                  <span className="cv-toc-num">{ch.chapterNumber}.</span>
                  <span className="cv-toc-title">{ch.chapterTitle}</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Center Primary Article Pane (100% SEO Indexable) */}
        <main className="cv-main-pane">
          {loading ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '80px' }}>
              <div className="loader" style={{ margin: '0 auto 15px' }} />
              <p style={{ color: 'var(--text-secondary)' }}>Loading chapter content...</p>
            </div>
          ) : error ? (
            <div className="cv-article-card" style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
              <h3>⚠️ Chapter Not Found</h3>
              <p>{error}</p>
              <button type="button" className="cv-control-btn" onClick={loadData} style={{ marginTop: '1rem' }}>
                Retry
              </button>
            </div>
          ) : currentChapter ? (
            <article
              ref={articleRef}
              className={`cv-article-card theme-${theme}`}
              onMouseUp={handleTextSelection}
              style={{
                ...(sidebarHeight ? { height: `${sidebarHeight}px`, maxHeight: `${sidebarHeight}px` } : {})
              }}
            >
              <header className="cv-article-header">
                <span className="cv-article-category">{bookInfo?.title || 'Sushruta Samhita'}</span>
                <h1 className="cv-article-title">
                  Chapter {currentChapter.chapterNumber}: {currentChapter.chapterTitle}
                </h1>
                <div className="cv-article-meta">
                  <span>Author: {bookInfo?.author || 'Maharishi Sushruta'}</span>
                </div>
              </header>

              <div
                className="cv-article-body"
                style={{ fontSize: `${fontSize}px` }}
                dangerouslySetInnerHTML={{ __html: currentChapter.contentHtml || currentChapter.content }}
              />

              <nav className="cv-stepper" aria-label="Chapter Stepper Navigation">
                {prevChapter ? (
                  <button type="button" className="cv-stepper-btn" onClick={() => navigateToChapter(prevChapter)}>
                    <ChevronLeft size={20} />
                    <div className="meta">
                      <span className="label">Previous</span>
                      <span className="title">{prevChapter.chapterTitle}</span>
                    </div>
                  </button>
                ) : <div />}

                {nextChapter ? (
                  <button type="button" className="cv-stepper-btn" onClick={() => navigateToChapter(nextChapter)} style={{ marginLeft: 'auto' }}>
                    <div className="meta" style={{ textAlign: 'right' }}>
                      <span className="label">Next</span>
                      <span className="title">{nextChapter.chapterTitle}</span>
                    </div>
                    <ChevronRight size={20} />
                  </button>
                ) : null}
              </nav>
            </article>
          ) : null}
        </main>

        {/* Right Sidebar Widget Column / Mobile Drawer */}
        <aside className={`cv-right-sidebar ${mobileAiOpen ? 'mobile-open' : ''}`} ref={rightSidebarRef}>
          {/* Dedicated Vedic AI Assistant Side Pane */}
          <div className="cv-sidebar-widget cv-ai-widget" style={{ border: '1px solid rgba(0, 212, 255, 0.25)', background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="var(--primary-color)" />
                <h4 style={{ margin: 0, fontSize: '0.98rem', border: 'none', padding: 0 }}>Vedic AI Assistant</h4>
              </div>
              <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-color)', background: 'rgba(0, 212, 255, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                Live
              </span>
            </div>

            {selectedText && (
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', borderLeft: '3px solid var(--primary-color)', fontSize: '0.84rem', lineHeight: 1.4 }}>
                <span style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>Selected Passage</span>
                "{selectedText}"
              </div>
            )}

            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('translate')}
                disabled={aiLoading}
              >
                Translate Verse
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('commentary')}
                disabled={aiLoading}
              >
                Commentary
              </button>
              <button
                type="button"
                className="cv-control-btn"
                style={{ fontSize: '0.76rem', padding: '4px 8px' }}
                onClick={() => handleAiAction('surgical')}
                disabled={aiLoading}
              >
                Surgical App
              </button>
            </div>

            {/* AI Response Output Area */}
            <div style={{ minHeight: '120px', maxHeight: '240px', overflowY: 'auto', background: 'rgba(0,0,0,0.25)', padding: '10px', borderRadius: '8px', fontSize: '0.86rem', lineHeight: 1.55, marginBottom: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {aiLoading ? (
                <div style={{ textAlign: 'center', padding: '15px 0' }}>
                  <p style={{ fontSize: '0.82rem', opacity: 0.8, margin: 0 }}>🧠 Analyzing classical verse with Vedic AI...</p>
                </div>
              ) : aiError ? (
                <p style={{ color: '#ef4444', margin: 0, fontSize: '0.84rem' }}>⚠️ {aiError}</p>
              ) : aiResponse ? (
                <div>{aiResponse}</div>
              ) : chatHistory.length > 0 ? (
                <div>{chatHistory[0]?.content}</div>
              ) : (
                <p style={{ opacity: 0.7, margin: 0, fontSize: '0.82rem' }}>
                  Highlight any text in the chapter or click an action button above to get instant AI translations & surgical commentary.
                </p>
              )}
            </div>

            {/* Interactive Chat Input */}
            <form
              onSubmit={handleSendChatMessage}
              style={{ display: 'flex', gap: '6px' }}
            >
              <input
                type="text"
                placeholder="Ask follow-up question..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                style={{ flex: 1, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', padding: '6px 10px', fontSize: '0.82rem', outline: 'none' }}
              />
              <button
                type="submit"
                className="cv-control-btn active"
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                disabled={aiLoading || !chatMessage.trim()}
              >
                Ask AI
              </button>
            </form>
          </div>

          {/* Book Metadata Card */}
          <div className="cv-sidebar-widget">
            <h4>Book Summary</h4>
            <p style={{ fontSize: '0.86rem', opacity: 0.85, lineHeight: 1.6 }}>
              {bookInfo?.description || 'Classical text detailing surgical anatomy, instrumentation, and clinical management.'}
            </p>
          </div>

          {/* Ad Banner Widget */}
          <div className="cv-sidebar-widget" style={{ padding: '0.5rem', textAlign: 'center' }}>
            <AdBanner type="square" />
          </div>
        </aside>

      </div>

      {/* Login Prompt Modal for Guests */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in for AI Features"
        message="Create a free account to use Vedic AI Assistant, save reading bookmarks, and participate in surgical discussions."
      />
    </div>
  );
};

export default ChapterViewer;
